import {
  AuditTrailEntryResponse,
  AuditTrailParams,
  useGetAuditTrailQuery,
} from "@/services/conciliation-audit";
import { useMemo, useState } from "react";
import { IsoPeriod } from "@/utils/date-range";
import {
  AuditFilters,
  AUDIT_PAGE_SIZE,
  EMPTY_AUDIT_FILTERS,
  FIRST_PAGE,
} from "../constants";

export type { AuditFilters };

export interface AuditTrailResult {
  filters: AuditFilters;
  setFilter: (key: keyof AuditFilters, value: string) => void;
  setPeriod: (period: IsoPeriod) => void;
  clearFilters: () => void;
  rows: AuditTrailEntryResponse[];
  total: number;
  perPage: number;
  setPerPage: (perPage: number) => void;
  currentPage: number;
  lastPage: number;
  hasPrevious: boolean;
  hasNext: boolean;
  isLoading: boolean;
  goToPreviousPage: () => void;
  goToNextPage: () => void;
}

const EMPTY_ROWS: AuditTrailEntryResponse[] = [];

export const useAuditTrail = (): AuditTrailResult => {
  const [filters, setFilters] = useState<AuditFilters>(EMPTY_AUDIT_FILTERS);
  const [currentPage, setCurrentPage] = useState<number>(FIRST_PAGE);
  const [perPage, setPerPageState] = useState<number>(AUDIT_PAGE_SIZE);

  const params: AuditTrailParams = useMemo(
    (): AuditTrailParams => ({
      ...filters,
      page: currentPage,
      perPage,
    }),
    [filters, currentPage, perPage],
  );

  const { data, isFetching } = useGetAuditTrailQuery(params);

  const lastPage: number = data?.meta.lastPage ?? FIRST_PAGE;

  const setFilter = (key: keyof AuditFilters, value: string): void => {
    setCurrentPage(FIRST_PAGE);
    setFilters((current: AuditFilters) => ({ ...current, [key]: value }));
  };

  const setPeriod = (period: IsoPeriod): void => {
    setCurrentPage(FIRST_PAGE);
    setFilters((current: AuditFilters) => ({ ...current, ...period }));
  };

  return {
    filters,
    setFilter,
    setPeriod,
    clearFilters: (): void => {
      setCurrentPage(FIRST_PAGE);
      setFilters(EMPTY_AUDIT_FILTERS);
    },
    rows: data?.data ?? EMPTY_ROWS,
    total: data?.meta.total ?? 0,
    perPage,
    setPerPage: (next: number): void => {
      setCurrentPage(FIRST_PAGE);
      setPerPageState(next);
    },
    currentPage,
    lastPage,
    hasPrevious: currentPage > FIRST_PAGE,
    hasNext: currentPage < lastPage,
    isLoading: isFetching,
    goToPreviousPage: (): void =>
      setCurrentPage((current: number) => Math.max(FIRST_PAGE, current - 1)),
    goToNextPage: (): void => setCurrentPage((current: number) => current + 1),
  };
};
